import { useEffect, useMemo, useRef, useState } from 'react';

import { SearchField } from './ui/SearchField.js';
import { SegmentedFilter } from './ui/SegmentedFilter.js';
import { StatusDot, type WorkState } from './ui/StatusPill.js';
import { navigatorKeyDown } from './ui/SplitWorkspace.js';

export type SceneConfidence = 'RESOLVED' | 'LOW_CONFIDENCE' | 'NOT_RECOGNISED';

/**
 * Where a scene stands in THIS workflow's review — the container's call, from
 * the rows it holds (`ReviewRequirements`): on the Locations pages a scene
 * needs attention while it has no requirement or one still awaits a decision;
 * on the detailed breakdown while its sheet is not marked reviewed.
 */
export type RailState = 'ATTENTION' | 'REVIEWED';

export type RailFilter = 'ALL' | RailState;

export interface RailScene {
  readonly id: string;
  readonly sceneNumber: string | null;
  /**
   * Where it sits in the screenplay, 1-based.
   *
   * THE FALLBACK, and not an ornament: a great many screenplays carry no scene
   * numbers at all until they are locked, and this rail showed an em dash on
   * every one of a hundred and twenty rows — a column of identical dashes where
   * the one piece of information that tells two "INT. BEDROOM — NIGHT" apart
   * should be.
   */
  readonly ordinal: number;
  readonly slugline: string | null;
  readonly confidence: SceneConfidence;
  /** The resolved location, or why it did not resolve. */
  readonly note: string | null;
  /**
   * The other scenes sharing this scene's location requirement.
   *
   * Shown because it is the single most load-bearing fact on this screen: a
   * producer editing "Hotel room" in scene 3 is editing scenes 8, 14 and 21 at
   * the same time, and nothing else on the page would tell them so before they
   * typed.
   */
  readonly sharedWithSceneNumbers: readonly string[];
  /** True when the shared group is itself unresolved. */
  readonly sharedUnresolved?: boolean;
  /** Needs attention or reviewed, in this workflow. Absent: read from the confidence. */
  readonly state?: RailState;
  /** How many location requirements the scene carries; absent where the workflow has none to count. */
  readonly requirementCount?: number;
}

export type RailSort = 'SCRIPT_ORDER' | 'BY_LOCATION';

const FLAG: Record<SceneConfidence, string | null> = {
  RESOLVED: null,
  LOW_CONFIDENCE: 'Low confidence',
  NOT_RECOGNISED: 'Not recognised',
};

/** A scene's review state, read from its confidence when the container did not say. */
export function railStateOf(scene: RailScene): RailState {
  return scene.state ?? (scene.confidence === 'RESOLVED' ? 'REVIEWED' : 'ATTENTION');
}

/**
 * Every scene in the screenplay, as a place to stand — the navigator beside
 * the one scene being worked on.
 *
 * A PASS THROUGH THE SCREENPLAY, NOT A SEARCH. The list keeps every scene in
 * its own order; the filters narrow it to the scenes that need the producer
 * and back, and the counts on them answer "is there anything left?" before a
 * click. "Nothing to do here" stays a result the producer can see.
 *
 * TWO ORDERS, DELIBERATELY. Script order is how the story reads. By location is
 * how it will be SHOT — every scene sharing one place, together — and a
 * producer switching to it is asking a different question: not "is this scene
 * right?" but "is this one place, and is it one place everywhere?".
 *
 * THE SHARING LINE IS THE WARNING. Editing a shared requirement is editing
 * every scene that uses it, and this is the only place that says so before the
 * edit rather than after.
 */
export function SceneRail({
  scenes,
  sort,
  focusedSceneId,
  onSort,
  onFocus,
  picked,
  onPick,
  filter: controlledFilter,
  onFilter,
  attentionLabel = 'Needs attention',
  attentionState = 'NEEDS_ATTENTION',
}: {
  scenes: readonly RailScene[];
  sort: RailSort;
  focusedSceneId?: string | null;
  onSort: (sort: RailSort) => void;
  onFocus: (sceneId: string) => void;
  /**
   * WHICH SCENES ARE PICKED for a bulk action (owner review 2026-10-27, point
   * 5). Absent, the rail has no boxes — it is a place to stand, as it always
   * was. Present, every row has a box and the header picks every scene SHOWN:
   * the current search, filter and sort, never past them, so "select all"
   * cannot quietly include scenes the producer has just filtered out of view.
   */
  picked?: ReadonlySet<string>;
  onPick?: (sceneIds: readonly string[], on: boolean) => void;
  /** The review filter; uncontrolled when the screen does not hold it. */
  filter?: RailFilter;
  onFilter?: (filter: RailFilter) => void;
  /** What "needs attention" is called in this workflow ("Not reviewed" on the breakdown sheet). */
  attentionLabel?: string;
  /**
   * How a scene that needs the producer is drawn. On the Locations pages it is
   * a problem to solve (red); on the breakdown sheet an unreviewed scene is
   * simply work not yet done, and a column of red dots would say otherwise.
   */
  attentionState?: WorkState;
}) {
  const unrecognised = scenes.filter((s) => s.confidence === 'NOT_RECOGNISED').length;
  const [ownFilter, setOwnFilter] = useState<RailFilter>('ALL');
  const filter = controlledFilter ?? ownFilter;
  const setFilter = (next: RailFilter) => {
    if (onFilter === undefined) setOwnFilter(next);
    else onFilter(next);
  };

  /**
   * FINDING ONE SCENE IN NINETY-TWO.
   *
   * A bare number matches the SCENE NUMBER exactly rather than anywhere in the
   * text: typing 8 to find scene 8 must not bury it under 18, 80 and 82. Any
   * other query is a substring of the number, the slugline or what the scene was
   * recognised as.
   */
  const [q, setQ] = useState('');

  const matching = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (needle === '') return scenes;

    if (/^\d+$/.test(needle)) {
      const exact = scenes.filter(
        (s) => (s.sceneNumber ?? String(s.ordinal)).toLowerCase() === needle,
      );
      if (exact.length > 0) return exact;
    }

    return scenes.filter((s) =>
      [s.sceneNumber ?? String(s.ordinal), s.slugline, s.note].some((field) =>
        (field ?? '').toLowerCase().includes(needle),
      ),
    );
  }, [scenes, q]);

  const counts = useMemo(() => {
    const attention = matching.filter((s) => railStateOf(s) === 'ATTENTION').length;

    return { ALL: matching.length, ATTENTION: attention, REVIEWED: matching.length - attention };
  }, [matching]);

  const ordered = useMemo(() => {
    const filtered = filter === 'ALL' ? matching : matching.filter((s) => railStateOf(s) === filter);
    if (sort === 'SCRIPT_ORDER') return filtered;

    // Group by the shared location, keeping each group in script order and
    // ordering the groups by where they first appear — so "by location" still
    // reads forwards through the screenplay rather than alphabetically, which
    // would scatter a production's day out of sequence.
    const keyOf = (scene: RailScene): string =>
      scene.sharedWithSceneNumbers.length > 0
        ? [scene.sceneNumber ?? '', ...scene.sharedWithSceneNumbers].sort().join('|')
        : `solo:${scene.id}`;

    const groups = new Map<string, RailScene[]>();
    for (const scene of filtered) {
      const key = keyOf(scene);
      const bucket = groups.get(key);
      if (bucket) bucket.push(scene);
      else groups.set(key, [scene]);
    }
    return [...groups.values()].flat();
  }, [matching, sort, filter]);

  // The scene on the right stays in sight on the left: a selection made from
  // the header ("next scene that needs you") can land below the fold.
  const listRef = useRef<HTMLUListElement>(null);
  useEffect(() => {
    if (focusedSceneId === null || focusedSceneId === undefined) return;
    const item = listRef.current?.querySelector<HTMLElement>(`[data-testid="scene-rail-item-${focusedSceneId}"]`);
    item?.scrollIntoView?.({ block: 'nearest' });
  }, [focusedSceneId]);

  const ids = ordered.map((s) => s.id);

  return (
    <aside data-testid="scene-rail" className="rq-rail">
      <div className="ui-nav-head">
        <div className="rq-rail-title">
          <h2 className="ui-panel-title">Scenes</h2>
          <span className="mm" data-testid="scene-rail-count">
            {scenes.length} scenes
            {unrecognised > 0 ? ` · ${String(unrecognised)} not recognised` : ''}
          </span>
        </div>

        <SearchField
          value={q}
          onChange={setQ}
          placeholder="Find a scene — keyword or number"
          label="Find a scene by keyword or number"
          testId="scene-rail-search"
        />

        <SegmentedFilter<RailFilter>
          label="Show scenes"
          testIdPrefix="scene-rail-filter"
          value={filter}
          onChange={setFilter}
          options={[
            { id: 'ALL', label: 'All', count: counts.ALL },
            {
              id: 'ATTENTION',
              label: attentionLabel,
              count: counts.ATTENTION,
              tone: counts.ATTENTION > 0 && attentionState === 'NEEDS_ATTENTION' ? 'attention' : undefined,
            },
            { id: 'REVIEWED', label: 'Reviewed', count: counts.REVIEWED },
          ]}
        />

        <div className="rq-rail-tools">
          <div role="group" aria-label="Order scenes" className="rq-rail-sort">
            {(
              [
                ['SCRIPT_ORDER', 'Script order'],
                ['BY_LOCATION', 'By location'],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                data-testid={`scene-rail-sort-${value}`}
                aria-pressed={sort === value}
                onClick={() => onSort(value)}
              >
                {label}
              </button>
            ))}
          </div>

          {onPick === undefined || picked === undefined ? null : (
            <label className="rq-rail-pickall">
              <input
                type="checkbox"
                data-testid="scene-rail-pick-all"
                checked={ordered.length > 0 && ordered.every((scene) => picked.has(scene.id))}
                onChange={(e) => {
                  onPick(
                    ordered.map((scene) => scene.id),
                    e.target.checked,
                  );
                }}
              />
              {q.trim() === '' && filter === 'ALL' ? 'Select every scene' : `Select the ${String(ordered.length)} shown`}
            </label>
          )}
        </div>

        {q.trim() === '' ? null : (
          <p
            className="mm rq-rail-found"
            data-testid="scene-rail-search-count"
            // The result of every keystroke, and the only thing that changes when
            // a query matches nothing — otherwise the list just silently empties.
            role="status"
            aria-live="polite"
          >
            {matching.length === 0
              ? 'No scene matches that'
              : `${String(matching.length)} of ${String(scenes.length)} scenes`}
          </p>
        )}
      </div>

      <ul
        ref={listRef}
        data-testid="scene-rail-list"
        className="ui-nav-list"
        onKeyDown={navigatorKeyDown(ids, focusedSceneId ?? null, onFocus, (id) => `[data-testid="scene-rail-item-${id}"]`)}
      >
        {ordered.length === 0 && q.trim() === '' ? (
          <li className="rq-rail-none" data-testid="scene-rail-empty">
            {filter === 'ATTENTION' ? 'Nothing needs attention.' : 'No scene here yet.'}
          </li>
        ) : null}
        {ordered.map((scene) => {
          const focused = focusedSceneId === scene.id;
          const flag = FLAG[scene.confidence];
          const state = railStateOf(scene);

          return (
            <li key={scene.id} className="rq-rail-li">
              {onPick === undefined || picked === undefined ? null : (
                // Beside the row, not inside it: a box inside a button is two
                // controls in one, and pressing it would also move the focus.
                <label className="rq-rail-pick" data-on={picked.has(scene.id) ? '1' : '0'}>
                  <input
                    type="checkbox"
                    data-testid={`scene-rail-pick-${scene.id}`}
                    aria-label={`Select scene ${scene.sceneNumber ?? String(scene.ordinal)}`}
                    checked={picked.has(scene.id)}
                    onChange={(e) => {
                      onPick([scene.id], e.target.checked);
                    }}
                  />
                </label>
              )}
              <button
                type="button"
                className="ui-row rq-rail-row"
                data-testid={`scene-rail-item-${scene.id}`}
                data-focused={focused ? '1' : '0'}
                data-state={state}
                aria-current={focused ? 'true' : undefined}
                // One stop in the tab order: the arrows move within the list.
                tabIndex={focused || (focusedSceneId == null && scene.id === ids[0]) ? 0 : -1}
                onClick={() => onFocus(scene.id)}
              >
                <span className="ui-row-num">{scene.sceneNumber ?? String(scene.ordinal)}</span>
                <span className="ui-row-main">
                  <span className="ui-row-title">{scene.slugline ?? 'Untitled scene'}</span>
                  {flag !== null || scene.note ? (
                    <span className="ui-row-sub">
                      {flag === null ? null : (
                        <span className="rq-flag" data-kind={scene.confidence} data-testid={`scene-rail-badge-${scene.id}`}>
                          {flag}
                        </span>
                      )}
                      {scene.note}
                    </span>
                  ) : null}
                  {scene.sharedWithSceneNumbers.length > 0 ? (
                    <span className="ui-row-sub rq-rail-shared" data-testid={`scene-rail-shared-${scene.id}`}>
                      {scene.sharedUnresolved === true ? 'Same unresolved location as ' : 'Same location as '}
                      {scene.sharedWithSceneNumbers.length === 1 ? 'scene ' : 'scenes '}
                      {scene.sharedWithSceneNumbers.join(', ')}
                    </span>
                  ) : null}
                </span>
                <span className="ui-row-end">
                  {scene.requirementCount === undefined ? null : (
                    <span
                      className="rq-count"
                      data-testid={`scene-rail-reqs-${scene.id}`}
                      title={`${String(scene.requirementCount)} location requirement${scene.requirementCount === 1 ? '' : 's'}`}
                      aria-label={`${String(scene.requirementCount)} location requirement${scene.requirementCount === 1 ? '' : 's'}`}
                    >
                      {String(scene.requirementCount)}
                    </span>
                  )}
                  <StatusDot
                    state={state === 'REVIEWED' ? 'CONFIRMED' : attentionState}
                    label={state === 'REVIEWED' ? 'Reviewed' : attentionLabel}
                  />
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}

/**
 * What Must and Nice actually DO, next to where they are set.
 *
 * These are not emphasis levels, they are matching behaviour: a Must removes
 * candidates and a Nice only reorders them. A producer who reads them as "very
 * important" and "quite important" will mark everything Must and get no
 * matches, then conclude the inventory is empty — so the consequence is stated
 * where the choice is made rather than in documentation nobody opens.
 */
export function ValueLegend() {
  const rows: readonly [string, string, string][] = [
    ['Must', '#232323', 'Only locations with this are shown'],
    ['Nice', '#475467', 'Ranks matches higher, removes nothing'],
    ['✕', '#b42318', 'Removes the value — matching stops looking for it'],
  ];

  return (
    <dl data-testid="value-legend" className="rq-legend">
      {rows.map(([term, bg, meaning]) => (
        <div key={term}>
          <dt style={{ background: bg }}>{term}</dt>
          <dd className="mm">{meaning}</dd>
        </div>
      ))}
    </dl>
  );
}
